import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import pb from '../../lib/pocketbaseClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const PortfolioForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    location: '',
    mainCategory: '',
    subCategory: '',
    projectYear: '',
    areaSize: '',
    budget: '',
    client: '',
    shortDescription: '',
    fullDescription: '',
    thumbnail: null,
    images: [],
    tags: '',
    featured: false,
    status: 'draft',
  });

  useEffect(() => {
    if (id) {
      setLoading(true);
      pb.collection('portfolios')
        .getOne(id)
        .then((record) => {
          setForm({
            title: record.title || '',
            slug: record.slug || '',
            location: record.location || '',
            mainCategory: record.mainCategory || '',
            subCategory: record.subCategory || '',
            projectYear: record.projectYear || '',
            areaSize: record.areaSize || '',
            budget: record.budget || '',
            client: record.client || '',
            shortDescription: record.shortDescription || '',
            fullDescription: record.fullDescription || '',
            thumbnail: null,
            images: [],
            tags: Array.isArray(record.tags) ? record.tags.join(', ') : record.tags || '',
            featured: record.featured || false,
            status: record.status || 'draft',
          });
        })
        .catch((err) => {
          console.error('Failed to load project:', err);
          toast.error('Project not found');
          navigate('/portfolio');
        })
        .finally(() => setLoading(false));
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      if (name === 'thumbnail') {
        setForm({ ...form, thumbnail: files[0] });
      } else if (name === 'images') {
        setForm({ ...form, images: Array.from(files) });
      }
    } else if (type === 'checkbox') {
      setForm({ ...form, [name]: checked });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleQuillChange = (value) => {
    setForm({ ...form, fullDescription: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('title', form.title);
      data.append('slug', form.slug);
      data.append('location', form.location);
      data.append('mainCategory', form.mainCategory);
      data.append('subCategory', form.subCategory);
      data.append('projectYear', form.projectYear);
      data.append('areaSize', form.areaSize);
      data.append('budget', form.budget);
      data.append('client', form.client);
      data.append('shortDescription', form.shortDescription);
      data.append('fullDescription', form.fullDescription);
      if (form.thumbnail) data.append('thumbnail', form.thumbnail);
      form.images.forEach((img) => data.append('images', img));
      data.append(
        'tags',
        JSON.stringify(form.tags.split(',').map((t) => t.trim()).filter(Boolean))
      );
      data.append('featured', form.featured);
      data.append('status', form.status);

      if (id) {
        await pb.collection('portfolios').update(id, data);
        toast.success('Project updated successfully');
      } else {
        await pb.collection('portfolios').create(data);
        toast.success('Project created successfully');
      }
      navigate('/portfolio');
    } catch (err) {
      console.error('Submit failed:', err);
      toast.error(err?.data?.message || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'blockquote', 'code-block'],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'blockquote', 'code-block',
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="px-4 py-8 md:px-8 lg:px-12">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/portfolio')}
          className="p-2 hover:bg-slate-100 rounded-full transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-slate-800">
          {id ? 'Edit Project' : 'Add New Project'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              placeholder="Modern Villa Project"
            />
          </div>
          <div>
            <Label htmlFor="slug">Slug * (URL friendly)</Label>
            <Input
              id="slug"
              name="slug"
              value={form.slug}
              onChange={handleChange}
              required
              placeholder="modern-villa-project"
            />
          </div>
          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Kolkata, India"
            />
          </div>
          <div>
            <Label htmlFor="mainCategory">Main Category</Label>
            <Input
              id="mainCategory"
              name="mainCategory"
              value={form.mainCategory}
              onChange={handleChange}
              placeholder="Architecture"
            />
          </div>
          <div>
            <Label htmlFor="subCategory">Sub Category</Label>
            <Input
              id="subCategory"
              name="subCategory"
              value={form.subCategory}
              onChange={handleChange}
              placeholder="Villa"
            />
          </div>
          <div>
            <Label htmlFor="projectYear">Year</Label>
            <Input
              id="projectYear"
              name="projectYear"
              value={form.projectYear}
              onChange={handleChange}
              placeholder="2024"
            />
          </div>
          <div>
            <Label htmlFor="areaSize">Area Size</Label>
            <Input
              id="areaSize"
              name="areaSize"
              value={form.areaSize}
              onChange={handleChange}
              placeholder="2500 sq.ft"
            />
          </div>
          <div>
            <Label htmlFor="budget">Budget</Label>
            <Input
              id="budget"
              name="budget"
              value={form.budget}
              onChange={handleChange}
              placeholder="₹35 Lakhs"
            />
          </div>
          <div>
            <Label htmlFor="client">Client</Label>
            <Input
              id="client"
              name="client"
              value={form.client}
              onChange={handleChange}
              placeholder="Mr. Sharma"
            />
          </div>
          <div>
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input
              id="tags"
              name="tags"
              value={form.tags}
              onChange={handleChange}
              placeholder="modern, luxury, villa"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="shortDescription">Short Description</Label>
          <Input
            id="shortDescription"
            name="shortDescription"
            value={form.shortDescription}
            onChange={handleChange}
            placeholder="Brief summary of the project"
          />
        </div>

        <div>
          <Label htmlFor="fullDescription">Full Description</Label>
          <div className="mt-1">
            <ReactQuill
              theme="snow"
              value={form.fullDescription}
              onChange={handleQuillChange}
              modules={modules}
              formats={formats}
              placeholder="Write a detailed description here…"
              className="bg-white rounded-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="thumbnail">Thumbnail Image</Label>
            <Input
              id="thumbnail"
              name="thumbnail"
              type="file"
              accept="image/*"
              onChange={handleChange}
            />
          </div>
          <div>
            <Label htmlFor="images">Gallery Images (multiple)</Label>
            <Input
              id="images"
              name="images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-5">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="featured"
              name="featured"
              checked={form.featured}
              onChange={handleChange}
              className="w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-500"
            />
            <Label htmlFor="featured" className="mb-0 cursor-pointer">
              Featured Project
            </Label>
          </div>
          <div>
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              name="status"
              value={form.status}
              onChange={handleChange}
              className="ml-3 px-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-500/20 outline-none bg-white"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/portfolio')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" /> {id ? 'Update' : 'Create'} Project
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PortfolioForm;